import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';

import moment from 'moment';
import { CSVFormatterService } from '../../../core/services/csv-formatter';
import { ServiceGroup } from '../../../models/service-group.model';
import { Service } from '../../../models/service.model';

import { LocationNameComponent } from '../location-name/location-name.component';

const validGroupByValues: Record<string, string> = {
  Location: 'Z_Location__r.Name',
  Service: 'RecordType.Name',
  Status: 'Status__c',
};

const groupByLabelsByValue = Object.entries(validGroupByValues).reduce(
  (labels, [label, value]) => ({ ...labels, [value]: label }),
  {} as Record<string, string>,
);

interface ServiceCsvRow {
  NIT: string;
  'Bundle Type': string;
  Status: string;
  Customer: string;
  'Customer PON': string;
  'Customer Site ID': string;
  'End User Company': string;
  'Location Name': string;
  'Street Address': string;
  City: string;
  State: string;
  ZIP: string;
  'Local Contact': string;
  'Sales Representative': string;
  'Account Manager': string;
  'Connector Type': string | undefined;
  Bandwidth: string | undefined;
  Term: string;
  'End of Term': Date | string;
  Provider: string;
  'Provider Circuit ID': string;
  'WAN IP Block': string;
  'WAN Mask': string;
  'LAN IP Block': string;
  'LAN Mask': string;
  Dmarc: string;
  Equipment: string;
  'Start of Service': Date | string;
  'End of Service': Date | string;
  'Activation Completed Date': string;
  'Billing Initiated Date': Date | string;
  MRC: number | string;
}

@Component({
  selector: 'app-inventory-list',
  templateUrl: './inventory-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class InventoryListComponent implements OnInit, OnChanges {
  @Input() services: Service[] = [];

  @Input() isLoading: boolean;

  @Input() groupedServices: ServiceGroup[] = [];

  @Input() selectedService: Service;

  @Output() setSelectedService = new EventEmitter();

  @Input() isServicesInProgress: boolean;

  @Input() groupByFormControl: UntypedFormControl;

  @Input() isAscFormControl: UntypedFormControl;

  @Output() setLoadingIndicator = new EventEmitter();

  @Input() locationsFilterOptions: string[];

  @Input() locationsFilterFormControl: UntypedFormControl;

  @Input() serviceFilterOptions: string[];

  @Input() serviceFilterFormControl: UntypedFormControl;

  @Input() statusFilterOptions: string[];

  @Input() statusFilterFormControl: UntypedFormControl;

  isLocationsDropdownOpen = signal(false);
  isServiceDropdownOpen = signal(false);
  isStatusDropdownOpen = signal(false);
  isGroupByDropdownOpen = signal(false);
  isSortDropdownOpen = signal(false);
  isPageSizeDropdownOpen = signal(false);

  readonly groupByOptions = Object.keys(validGroupByValues);

  readonly sortOptions = [
    { label: 'A-Z', value: true },
    { label: 'Z-A', value: false },
  ];

  @HostListener('document:click')
  closeFilterDropdowns(): void {
    this.isLocationsDropdownOpen.set(false);
    this.isServiceDropdownOpen.set(false);
    this.isStatusDropdownOpen.set(false);
    this.isGroupByDropdownOpen.set(false);
    this.isSortDropdownOpen.set(false);
    this.isPageSizeDropdownOpen.set(false);
  }

  @Input() totalSelectedFilters = 0;

  @Input() searchTermControl: UntypedFormControl;

  page = 1;

  pageSizeControl = new UntypedFormControl(10);

  route = inject(Router);

  activatedRoute = inject(ActivatedRoute);

  cdr = inject(ChangeDetectorRef);

  dialog = inject(MatDialog);

  csvFormatter = inject(CSVFormatterService);

  private destroyRef = inject(DestroyRef);

  private serviceIdSet = new Set<string>();

  formattedCSVData: ServiceCsvRow[] = [];
  readonly pageSizeOptions = [10, 25, 50, 100];

  get pageSize(): number {
    return this.pageSizeControl.value || 10;
  }

  get totalPages(): number {
    return Math.ceil((this.groupedServices?.length || 0) / this.pageSize);
  }

  get paginatedServices(): ServiceGroup[] {
    if (!this.groupedServices) return [];
    const start = (this.page - 1) * this.pageSize;
    const end = start + this.pageSize;
    return this.groupedServices.slice(start, end);
  }

  get visibleServiceCount(): number {
    return (this.groupedServices ?? []).reduce(
      (total, group) => total + (group.services?.length ?? 0),
      0,
    );
  }

  expandedGroups = signal(new Set<string>());

  get currentGroupByLabel(): string {
    return groupByLabelsByValue[this.groupByFormControl?.value] ?? 'Location';
  }

  get currentSortLabel(): string {
    return this.isAscFormControl?.value ? 'A-Z' : 'Z-A';
  }

  ngOnInit(): void {
    this.activatedRoute.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        if (
          params.pageSize &&
          this.pageSizeOptions.includes(Number(params.pageSize))
        ) {
          this.pageSizeControl.setValue(Number(params.pageSize));
        }
      });

    // Sync page size to URL when it changes
    this.pageSizeControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((size) => {
        this.route.navigate([], {
          relativeTo: this.activatedRoute,
          queryParamsHandling: 'merge',
          replaceUrl: true,
          queryParams: {
            pageSize: size,
          },
        });
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('groupedServices' in changes && this.groupedServices?.length > 0) {
      this.serviceIdSet = new Set(
        this.groupedServices.flatMap((g) => g.services ?? []).map((s) => s.Id),
      );

      const isSelectionInList =
        !!this.selectedService?.Id &&
        this.serviceIdSet.has(this.selectedService.Id);

      if (!isSelectionInList) {
        this.onSelectService(this.groupedServices[0].services[0]);
      }

      this.expandFirstGroup();
    }

    if ('services' in changes && this.services?.length > 0) {
      this.formatCSVData();
    }
  }

  getFilterLabel(control: UntypedFormControl, placeholder: string): string {
    const selected: string[] = control?.value ?? [];
    if (selected.length === 0) return placeholder;
    return `${selected.length} selected`;
  }

  isFilterSelected(control: UntypedFormControl, value: string): boolean {
    return (control?.value ?? []).includes(value);
  }

  getFilterButtonClass(control: UntypedFormControl, isOpen: boolean): string {
    if (isOpen) return 'border-blue-400 bg-white text-black';
    if ((control?.value ?? []).length > 0) {
      return 'border-blue-500 bg-blue-75 text-blue-700';
    }
    return 'border-gray-400 bg-white text-black hover:border-gray-700 hover:bg-gray-50';
  }

  clearFilters(): void {
    this.locationsFilterFormControl?.setValue([]);
    this.serviceFilterFormControl?.setValue([]);
    this.statusFilterFormControl?.setValue([]);
    this.groupByFormControl?.setValue('Z_Location__r.Name');
    this.isAscFormControl?.setValue(true);
    this.closeFilterDropdowns();
    this.cdr.markForCheck();
  }

  toggleFilterValue(
    control: UntypedFormControl,
    value: string,
    event: MouseEvent,
  ): void {
    event.stopPropagation();
    const current: string[] = control?.value ?? [];
    const updated = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    control.setValue(updated);
    this.cdr.markForCheck();
  }

  toggleLocationsDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.isLocationsDropdownOpen.update((v) => !v);
    this.isServiceDropdownOpen.set(false);
    this.isStatusDropdownOpen.set(false);
    this.isGroupByDropdownOpen.set(false);
    this.isSortDropdownOpen.set(false);
  }

  toggleServiceDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.isServiceDropdownOpen.update((v) => !v);
    this.isLocationsDropdownOpen.set(false);
    this.isStatusDropdownOpen.set(false);
    this.isGroupByDropdownOpen.set(false);
    this.isSortDropdownOpen.set(false);
  }

  toggleStatusDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.isStatusDropdownOpen.update((v) => !v);
    this.isLocationsDropdownOpen.set(false);
    this.isServiceDropdownOpen.set(false);
    this.isGroupByDropdownOpen.set(false);
    this.isSortDropdownOpen.set(false);
  }

  toggleGroupByDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.isGroupByDropdownOpen.update((v) => !v);
    this.isLocationsDropdownOpen.set(false);
    this.isServiceDropdownOpen.set(false);
    this.isStatusDropdownOpen.set(false);
    this.isSortDropdownOpen.set(false);
  }

  toggleSortDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.isSortDropdownOpen.update((v) => !v);
    this.isLocationsDropdownOpen.set(false);
    this.isServiceDropdownOpen.set(false);
    this.isStatusDropdownOpen.set(false);
    this.isGroupByDropdownOpen.set(false);
  }

  toggleGroup(key: string): void {
    this.expandedGroups.update((set) => {
      const next = new Set(set);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  isGroupExpanded(key: string): boolean {
    return this.expandedGroups().has(key);
  }

  private expandFirstGroup(): void {
    const firstKey = this.paginatedServices[0]?.groupTitle;
    this.expandedGroups.set(firstKey ? new Set([firstKey]) : new Set());
  }

  onPageChange(newPage: number): void {
    this.page = newPage;
    this.expandFirstGroup();
  }

  togglePageSizeDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.isPageSizeDropdownOpen.update((v) => !v);
    this.isLocationsDropdownOpen.set(false);
    this.isServiceDropdownOpen.set(false);
    this.isStatusDropdownOpen.set(false);
    this.isGroupByDropdownOpen.set(false);
    this.isSortDropdownOpen.set(false);
  }

  closePageSizeDropdown(): void {
    this.isPageSizeDropdownOpen.set(false);
  }

  onSelectService(service: Service): void {
    if (service.Id) this.setSelectedService.emit(service.Id);
  }

  onHeaderClick(service: Service): void {
    const serviceDiv = document.getElementsByClassName('inventory-viewport');
    serviceDiv[0].scrollTop = 0;
    this.onSelectService(service);
  }

  onClickGroupBy(value: string): void {
    this.setLoadingIndicator.emit();
    const groupBy = validGroupByValues[value];

    if (this.groupByFormControl.value === groupBy) {
      this.isAscFormControl.setValue(
        Boolean(this.isAscFormControl.value) === false,
      );
    } else {
      this.groupByFormControl.setValue(groupBy);
      this.page = 1;
      this.cdr.markForCheck();
    }
  }

  selectGroupBy(value: string): void {
    this.isGroupByDropdownOpen.set(false);
    const groupBy = validGroupByValues[value];

    if (!groupBy || this.groupByFormControl.value === groupBy) {
      this.cdr.markForCheck();
      return;
    }

    this.onClickGroupBy(value);
  }

  selectSort(isAscending: boolean): void {
    this.isSortDropdownOpen.set(false);

    if (Boolean(this.isAscFormControl.value) === isAscending) {
      this.cdr.markForCheck();
      return;
    }

    this.setLoadingIndicator.emit();
    this.isAscFormControl.setValue(isAscending);
    this.page = 1;
    this.cdr.markForCheck();
  }

  formatCSVData(): void {
    if (!Array.isArray(this.services) || this.services.length === 0) {
      this.formattedCSVData = [];
      this.cdr.markForCheck();
      return;
    }

    const newCSVData = this.services.map((service): ServiceCsvRow => {
      const elementInfo = this.csvFormatter.getElementInfo(service);
      const loc = service.Z_Location__r;

      return {
        NIT: service.Name,
        'Bundle Type': service.RecordType?.Name ?? '',
        Status: service.Status__c === 'New' ? 'Pending' : service.Status__c,
        Customer: service.Account__r?.Name ?? '',
        'Customer PON':
          (service?.Orders__r?.length || 0) > 0
            ? service.Orders__r[0].Customer_PON__c
            : '',
        'Customer Site ID': service?.Z_Location_Customer_Side_Id__c ?? '',
        'End User Company': service?.Z_Location_Company_Name__c ?? '',
        'Location Name': service?.CustomerLocationAlias ?? '',
        'Street Address': loc?.Street_1__c
          ? `${loc.Street_1__c} ${loc.Street_2__c || ''}`
          : '',
        City: loc?.City__c ?? '',
        State: loc?.State__c ?? '',
        ZIP: loc?.Zip_Code__c ?? '',
        'Local Contact': service?.localContacts ?? '',
        'Sales Representative': service?.Sales_Rep__r?.Name ?? '',
        'Account Manager': service?.Account__r?.Account_Manager__r?.Name ?? '',
        'Connector Type': service.Connector_Type_Z_Location__c,
        Bandwidth: service.Asymmetrical_Bandwidth__c
          ? `${service.Bandwidth_Downstream__c} / ${service.Bandwidth_Upstream__c}`
          : service.Bandwidth_Downstream__c,
        Term: service?.Term__c ?? '',
        'End of Term': service?.Current_Term_End_Date__c ?? '',
        Provider:
          (service?.Vendor__r?.Display_Alias__c as string) ||
          (service?.Vendor__r?.Name as string) ||
          '',
        'Provider Circuit ID': elementInfo?.vendorCircuitId ?? '',
        'WAN IP Block': elementInfo?.wanIpBlock ?? '',
        'WAN Mask': elementInfo?.wanMask ?? '',
        'LAN IP Block': elementInfo?.lanIpBlock ?? '',
        'LAN Mask': elementInfo?.lanMask ?? '',
        Dmarc: elementInfo?.dmarcZLoc ?? '',
        Equipment: elementInfo?.equipmentName ?? '',
        'Start of Service': service?.Start_Of_Service__c ?? '',
        'End of Service': service?.End_Of_Service__c ?? '',
        'Activation Completed Date':
          service?.Activation_Date__c != null
            ? moment(service.Activation_Date__c).format('L')
            : '',
        'Billing Initiated Date': service?.First_Invoice__c ?? '',
        MRC: service?.MRR__c ?? '',
      };
    });

    this.formattedCSVData = newCSVData;
    this.cdr.markForCheck();
  }

  onEditLocationName(service: Service): void {
    const dialogConfig = {
      autoFocus: true,
      minHeight: '200px',
      maxHeight: 'calc(100vh - 20px)',
      width: '560px',
      maxWidth: 'calc(100vw - 20px)',
      data: {
        originPage: 'Service',
        locationId: service.Z_Location__c,
        locationAddress: service.Z_Location__r.Name,
        banLookupId: service.BanLookupId,
        customerLocationAlias: service.CustomerLocationAlias,
        services: this.services,
      },
      disableClose: false,
      ariaLabelledBy: 'location-name-dialog-title',
    };

    this.dialog.open(LocationNameComponent, dialogConfig);
  }

  hasObjectLength(dataObj: object, expectedLength: number): boolean {
    return Object.keys(dataObj ?? {}).length === expectedLength;
  }
}
